import { Injectable } from '@angular/core';
import * as Cesium from 'cesium';
import { EntityMapEntity } from './facility-pin.service';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import { UnitEntityAttribute } from './unit-entity-attribute.service';
import { WindowService } from './window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataContextService } from './data-context.service';
import { TimelineService } from './timeline.service';
 
export interface UnitStatus {
  longitude: number;
  latitude: number;
  altitude: number;
  timestamp: Date;
}
 
export interface SpeedHeadingData {
  speed: number;
  heading: number;
  timestamp: Date;
}
 
@Injectable({
  providedIn: 'root'
})
export class UnitMapEntityService {
  constructor() {}
}
 
export class UnitMapEntity extends EntityMapEntity {
  private static readonly TIME_SAMPLE_MILLI = 2000;
 
  protected sampledPositionProperty: Cesium.SampledPositionProperty;
  protected firstStatus: UnitStatus | undefined;
  protected endStatus: UnitStatus | undefined;
  protected endPosition: Cesium.Cartesian3 | undefined;
  protected showTracks: boolean = false;
  protected calcStats: boolean;
  protected pathResolution: number | undefined;
 
  // Temporal metadata
  protected speed: Cesium.SampledProperty;
  protected heading: Cesium.SampledProperty;
  protected speedApp: Cesium.SampledProperty;
  protected headingApp: Cesium.SampledProperty;
 
  public readonly VERTICAL_ORIGIN = Cesium.VerticalOrigin.CENTER;
  public readonly HORIZONTAL_ORIGIN = Cesium.HorizontalOrigin.CENTER;
 
  constructor(
    mapEntityName: string,
    mapEntityLayerPath: string,
    icon: string | undefined,
    calcStats: boolean = true,
    pathResolution: number | undefined,
    cameraService: CameraService,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal,
    timelineService: TimelineService
  ) {
    super(mapEntityName, mapEntityLayerPath, cameraService, coreService, windowService, dataContext, modalService, timelineService);

    if (icon === undefined) {
      this.setIcon('assets/img/default_unit.png');
    } else {
      this.setIcon(icon);
    }
 
    this.calcStats = calcStats;
    this.pathResolution = pathResolution;
 
    this.sampledPositionProperty = new Cesium.SampledPositionProperty();
    this.sampledPositionProperty.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
    this.sampledPositionProperty.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
 
    // Initialize temporal metadata
    this.speed = new Cesium.SampledProperty(Number);
    this.heading = new Cesium.SampledProperty(Number);
 
    this.speedApp = new Cesium.SampledProperty(Number);
    this.headingApp = new Cesium.SampledProperty(Number);
    this.speedApp.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
    this.speedApp.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
    this.headingApp.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
    this.headingApp.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
 
    // Add commands and attributes as needed
    // this.addCommand(new DefaultShowHideTracksCommand());
    if (calcStats) {
      this.addAttribute(new UnitEntityAttribute(this.id, this));
    }
  }
 
 createUnitStatus (lon: number, lat: number, alt: number, date: number): UnitStatus {
    return {
      longitude: lon,
      latitude: lat,
      altitude: alt,
      timestamp: new Date(date)
    };
  }
 
  addStatus(status: UnitStatus): void {
    if (this.firstStatus === undefined || status.timestamp < this.firstStatus.timestamp) {
      this.firstStatus = status;
    }
 
    const time = Cesium.JulianDate.fromDate(status.timestamp);
    let position: Cesium.Cartesian3;
   
    if (status.altitude !== undefined) {
      position = Cesium.Cartesian3.fromDegrees(status.longitude, status.latitude, status.altitude);
    } else {
      position = Cesium.Cartesian3.fromDegrees(status.longitude, status.latitude);
    }
   
    this.sampledPositionProperty.addSample(time, position);
 
    if (this.calcStats) {
      let prevPosition: Cesium.Cartesian3 | undefined;
      let sampleTime: number;
 
      // Newest time so use the previous time we stored
      if (this.endStatus && status.timestamp > this.endStatus.timestamp) {
        prevPosition = this.endPosition;
        sampleTime = status.timestamp.getTime() - this.endStatus.timestamp.getTime();
      } else {
        // No newest time so go back 2 seconds to interpolate
        const prevSampleTime = Cesium.JulianDate.fromDate(
          new Date(status.timestamp.getTime() - UnitMapEntity.TIME_SAMPLE_MILLI)
        );
        prevPosition = this.sampledPositionProperty.getValue(prevSampleTime);
        sampleTime = UnitMapEntity.TIME_SAMPLE_MILLI;
      }
 
      this.speed.addSample(time, this.calculateSpeed(prevPosition, position, sampleTime));
      this.heading.addSample(time, this.calculateHeading(prevPosition, position));
    }
 
    if (this.endStatus === undefined || status.timestamp > this.endStatus.timestamp) {
      this.endStatus = status;
      this.endPosition = position;
    }
 
    this.rebuildEntity();
  }
 
  addSpeedAndHeading(data: SpeedHeadingData): void {
    const time = Cesium.JulianDate.fromDate(data.timestamp);
    const speed = data.speed || 0.0;
    const heading = data.heading || 0.0;
   
    this.speedApp.addSample(time, speed);
    this.headingApp.addSample(time, heading);
  }
 
  override setIcon(icon: string): void {
    super.setIcon(icon);
 
    if (this.cesiumEntity !== undefined) {
      this.cesiumEntity.billboard.image = this.getIcon();
    }
  }
 
  override buildEntity(): void {
    super.buildEntity();
 
    // Use a model instead of billboard if needed
    const pinIcon = this.getIcon() === undefined ? 'assets/img/default_unit.png' : this.getIcon();
   
    this.cesiumEntity.billboard = {
      image: pinIcon,
      verticalOrigin: this.VERTICAL_ORIGIN,
      horizontalOrigin: this.HORIZONTAL_ORIGIN
    };
 
    // Show the path as a yellow line sampled in 1 second increments
    this.cesiumEntity.path = {
      resolution: this.pathResolution || 1,
      material: new Cesium.PolylineGlowMaterialProperty({
        glowPower: 0.1,
        color: Cesium.Color.YELLOW
      }),
      width: 5,
      show: this.showTracks,
      leadTime: 0
    };
 
    this.rebuildEntity();
  }
 
  rebuildEntity(): void {
    if (this.cesiumEntity === undefined) {
      this.buildEntity();
    } else if (this.firstStatus && this.endStatus && this.endPosition) {
      this.timestamp = this.firstStatus.timestamp;
 
      const julianStartDate = Cesium.JulianDate.fromDate(this.firstStatus.timestamp);
      const julianEndDate = Cesium.JulianDate.fromDate(this.endStatus.timestamp);
 
      const property = new Cesium.CompositePositionProperty();
      property.intervals.addInterval(new Cesium.TimeInterval({
        start: Cesium.Iso8601.MINIMUM_VALUE,
        stop: julianEndDate,
        data: this.sampledPositionProperty
      }));
 
      // Constant interval for after the statuses are finished
      property.intervals.addInterval(new Cesium.TimeInterval({
        start: julianEndDate,
        stop: Cesium.Iso8601.MAXIMUM_VALUE,
        data: this.addConstantPosition(this.endPosition)
      }));
 
      this.cesiumEntity.position = property;
 
      const pinIcon = this.getIcon() === undefined ? 'assets/img/default_unit.png' : this.getIcon();
      this.cesiumEntity.billboard.image = pinIcon;
      this.cesiumEntity.orientation = new Cesium.VelocityOrientationProperty(this.sampledPositionProperty);
      this.cesiumEntity.availability = new Cesium.TimeIntervalCollection([
        new Cesium.TimeInterval({
          start: julianStartDate,
          stop: Cesium.Iso8601.MAXIMUM_VALUE
        })
      ]);
    }
  }
 
  private addConstantPosition(endPosition: Cesium.Cartesian3): Cesium.ConstantPositionProperty {
    const constantProperty = new Cesium.ConstantPositionProperty();
    constantProperty.setValue(endPosition);
    return constantProperty;
  }
 
  showTrail(): void {
    this.showTracks = true;
    if (this.cesiumEntity !== undefined) {
      this.cesiumEntity.path.show = true;
    }
  }
 
  hideTrail(): void {
    this.showTracks = false;
    if (this.cesiumEntity !== undefined) {
      this.cesiumEntity.path.show = false;
    }
  }
 
  isTrailVisible(): boolean {
    return this.showTracks;
  }
 
  override getCesiumEntity(): Cesium.Entity {
    if (this.cesiumEntity === undefined) {
      this.buildEntity();
    }
    return this.cesiumEntity;
  }
 
  getSpeed(): Cesium.SampledProperty {
    return this.speed;
  }
 
  getSpeedApp(): Cesium.SampledProperty {
    return this.speedApp;
  }
 
  getHeading(): Cesium.SampledProperty {
    return this.heading;
  }
 
  getHeadingApp(): Cesium.SampledProperty {
    return this.headingApp;
  }
 
  getSampledPositions(): Cesium.SampledPositionProperty {
    return this.sampledPositionProperty;
  }
 
  getLastKnownPosition(): Cesium.Cartesian3 | undefined {
    return this.endPosition;
  }
 
  calculateSpeed(prevSample: Cesium.Cartesian3 | undefined, nextSample: Cesium.Cartesian3, timeDifference: number): number {
    if (prevSample !== undefined && nextSample !== undefined) {
      const distanceInKm = Cesium.Cartesian3.distance(prevSample, nextSample) * 0.001;
      return (distanceInKm / (timeDifference / 1000)) * 3600;
    }
    return 0;
  }
 
  calculateHeading(prevSample: Cesium.Cartesian3 | undefined, nextSample: Cesium.Cartesian3): number {
    if (prevSample !== undefined && nextSample !== undefined) {
      const loc1 = Cesium.Cartographic.fromCartesian(prevSample);
      const loc2 = Cesium.Cartographic.fromCartesian(nextSample);
 
      return Math.atan2(
        Cesium.Math.toDegrees(loc2.longitude) - Cesium.Math.toDegrees(loc1.longitude),
        Cesium.Math.toDegrees(loc2.latitude) - Cesium.Math.toDegrees(loc1.latitude)
      ) * 180 / Math.PI;
    }
    return 0;
  }
 
  override getCenterLatitude(): number | undefined {
    return this.endStatus?.latitude;
  }
 
  override getCenterLongitude(): number | undefined {
    return this.endStatus?.longitude;
  }
 
  override getCenterAltitude(): number | undefined {
    return this.endStatus?.altitude;
  }
}
 